// Section 24.5: the base value of every Target, and the one function phases and explain() use
// to compute a number: evaluate(baseOf(target), collect(target)). Adding a Target kind means
// adding a case here; the switch is exhaustive.
import { collect, evaluate, evaluateValue } from '../../core/modifiers';
import { clamp } from '../../core/math';
import { ageGroupOf, needsOf } from '../../core/queries';
import { assertNever } from '../../core/errors';
import type { Config } from '../../config';
import type {
  DemandResource,
  Evaluation,
  HealthLossCause,
  Person,
  ReadContext,
  Target,
  TrustCause,
} from '../../core/types';

function demandBase(ctx: ReadContext, p: Person, resource: DemandResource): number {
  const cfg = ctx.cfg;
  switch (resource) {
    case 'food': {
      const group = ageGroupOf(cfg, p.ageSeasons);
      return group === 'child'
        ? cfg.needs.foodChild
        : group === 'elder'
          ? cfg.needs.foodElder
          : cfg.needs.foodAdult;
    }
    case 'water':
      return cfg.needs.waterPerPerson;
    case 'warmth':
      return cfg.needs.warmthPerBed[ctx.season.season] * cfg.needs.bedsPerPerson;
    case 'medicine':
      return p.status.sick !== null || p.status.injured !== null ? cfg.needs.medicinePerPatient : 0;
  }
}

/** Section 23 health terms that depend on how long a need has gone unmet. */
function healthLossBase(ctx: ReadContext, p: Person, cause: HealthLossCause): number {
  const cfg = ctx.cfg;
  const s = p.streaks;
  switch (cause) {
    case 'hunger':
      return s.hungry >= 2 ? cfg.bodies.healthHungry2 : 0;
    case 'thirst':
      return s.thirsty >= 2 ? cfg.bodies.healthDehydrated2 : 0;
    case 'exposure': {
      if (
        ctx.season.season !== 'winter' ||
        needsOf(ctx.state, p.id).shelter >= cfg.needs.shortThreshold
      )
        return 0;
      return s.exposed >= 2 ? cfg.bodies.healthExposedWinter : -cfg.needs.shelterWinterHealth1;
    }
    case 'cold':
      if (s.cold >= 2) return cfg.bodies.healthCold2;
      return s.cold === 1 ? -cfg.needs.warmthShortHealth1 : 0;
    case 'age':
      return ageGroupOf(cfg, p.ageSeasons) === 'elder' ? cfg.lifecycle.elderHealthDecay : 0;
    case 'illness': {
      const sick = p.status.sick === null ? 0 : cfg.illnesses[p.status.sick.illnessId].healthLoss;
      const hurt = p.status.injured === null ? 0 : cfg.illnesses.injury.healthLoss;
      return sick + hurt;
    }
  }
}

/** Section 16 trust table (and Sections 13, 14, 18 where they name trust changes). */
export function trustBase(cfg: Config, cause: TrustCause): number {
  const t = cfg.trust;
  switch (cause) {
    case 'rationed-short':
      return t.rationedShort;
    case 'rationed-short-equal':
      return t.rationedShortEqual;
    case 'rationed-short-deviation':
      // Section 17: the full penalty; the announce policy doubles it (config.rationing).
      return t.rationedShort;
    case 'child-rationed-short':
      return t.childRationedShort;
    case 'fed-full-while-others-short':
      return t.fedFullWhileOthersShort;
    case 'treated-short-medicine':
      return t.treatedShortMedicine;
    case 'passed-over-treatment':
      return t.passedOverTreatment;
    case 'punished':
      return 0;
    case 'watched-harsh-punishment':
      return cfg.justice.watchedHarshTrustMin;
    case 'crime-victim':
      return cfg.crime.assault.victimHouseholdTrust;
    case 'killing':
      return cfg.crime.killing.trust;
    case 'given-tool':
      return t.givenTool;
    case 'all-needs-met':
      return t.allNeedsMet;
    case 'promised-build':
      return t.promisedBuild;
    case 'festival':
      return t.festival;
    case 'deprivation-death':
      return t.deprivationDeathAll;
    case 'deprivation-death-household':
      return t.deprivationDeathHousehold;
    case 'policy-change':
      return 0;
    case 'safety-short':
      return cfg.needs.safetyShortTrust;
    case 'idle':
      return t.idlePerSeason;
    case 'charisma':
      // Section 5: the share of a Charismatic person's household trust change that spreads.
      return cfg.traits.charismatic.spreadMul;
    case 'norm-harsh-justice':
      // Section 16: zero unless the harsh justice norm's modifier applies (Gentle, Devout).
      return 0;
    case 'departure':
      return cfg.migration.departureTrust;
    case 'turned-away':
      return cfg.migration.turnAwayDevoutTrust;
  }
}

/** Section 15: 1% per season at 60 rising linearly to 12% at 85, extrapolated and capped. */
export function elderDeathBase(cfg: Config, ageSeasons: number): number {
  if (ageSeasons < cfg.people.elderAtSeasons) return 0;
  const l = cfg.lifecycle;
  const years = ageSeasons / cfg.people.seasonsPerYear;
  return clamp(
    l.elderDeathAt60 + ((l.elderDeathAt85 - l.elderDeathAt60) * (years - 60)) / 25,
    l.elderDeathAt60,
    l.elderDeathMax,
  );
}

export function baseOf(ctx: ReadContext, target: Target): number {
  const { cfg, registry, state } = ctx;
  const person = 'personId' in target ? state.people[target.personId] : undefined;
  switch (target.kind) {
    case 'output':
      return registry.jobs[target.jobId].baseYield;
    case 'jobRisk':
      return registry.jobs[target.jobId].risks[target.risk] ?? 0;
    case 'demand':
      return person === undefined ? 0 : demandBase(ctx, person, target.resource);
    case 'illnessRisk':
      return cfg.illness.baseChance;
    case 'illnessDeath':
      return registry.illnesses[target.illnessId].deathChance;
    case 'catchRisk':
      return cfg.jobs.healer.illness;
    case 'illnessMultiplier':
      return 1;
    case 'healthGain':
    case 'strengthChange':
    case 'emigrationPressure':
    case 'service':
      return 0;
    case 'healthLoss':
      return person === undefined ? 0 : healthLossBase(ctx, person, target.cause);
    case 'strengthCap':
      return cfg.people.statMax;
    case 'crimeRisk':
      return cfg.crime.baseChance;
    case 'happinessTarget':
      return cfg.happiness.base;
    case 'trustChange':
      return trustBase(cfg, target.cause);
    case 'skillGain':
      // Section 11: doing 0.15, school 0.25, apprenticeship 0.35 per season.
      return target.via === 'school'
        ? cfg.skills.gainSchool
        : target.via === 'apprentice'
          ? cfg.skills.gainApprentice
          : cfg.skills.gainDoing;
    case 'elderDeath':
      return person === undefined ? 0 : elderDeathBase(cfg, person.ageSeasons);
    case 'conception':
      return cfg.lifecycle.conceptionBase;
    case 'buildCost':
      return registry.buildings[target.buildingType].buildPoints;
    case 'buildingEffect': {
      const b = state.buildings[target.buildingId];
      return b === undefined ? 0 : 1;
    }
    case 'spoilage':
      return cfg.resources.spoilage[target.resource];
    case 'capacity':
      return cfg.resources.baseCapacity[target.resource];
    case 'deterrence':
    case 'harvestYield':
      return 1;
    case 'eventWeight':
      return registry.events[target.eventId].baseWeight;
    default:
      return assertNever(target, 'target');
  }
}

/** The one way to compute a number (24.5). */
export function valueOf(ctx: ReadContext, target: Target): Evaluation {
  return evaluate(baseOf(ctx, target), collect(ctx, target));
}

/** The value alone (no factor rows): identical to valueOf(ctx, target).value, cheaper. */
export function num(ctx: ReadContext, target: Target): number {
  return evaluateValue(baseOf(ctx, target), collect(ctx, target));
}
